/**
 * API service for communicating with the backend (JWT auth)
 */

const API_BASE = process.env.REACT_APP_API_BASE || "http://localhost:8081";

// Types matching backend responses
export interface ApiResponse<T = any> {
  success: boolean;
  data?: T;
  error?: string;
  timestamp: string;
}

export interface LoginRequest {
  pin: string;
}

export interface LoginResponse {
  user: {
    id: string;
    roles: string[];
  };
  token: string;
  expiresAt: number;
}

export interface User {
  id: string;
  roles: string[];
}

// JWT token storage
const TOKEN_KEY = "sde_jwt_token";

function getToken(): string | null {
  return localStorage.getItem(TOKEN_KEY);
}

function setToken(token: string | null) {
  if (token) localStorage.setItem(TOKEN_KEY, token);
  else localStorage.removeItem(TOKEN_KEY);
}

// Error handling utility
export class ApiError extends Error {
  constructor(message: string, public status?: number, public code?: string) {
    super(message);
    this.name = "ApiError";
  }
}

// Generic API request function
async function apiRequest<T = any>(path: string, options: RequestInit = {}): Promise<ApiResponse<T>> {
  const url = `${API_BASE}${path}`;

  // Default headers
  const headers: Record<string, string> = {
    "Content-Type": "application/json",
  };

  // Add any existing headers
  if (options.headers) Object.assign(headers, options.headers as Record<string, string>);

  // Attach JWT if present
  const token = getToken();
  if (token) headers["Authorization"] = `Bearer ${token}`;

  const response = await fetch(url, {
    ...options,
    headers,
    credentials: "omit", // JWT is sent in Authorization header
  });

  let data: any = null;
  try {
    data = await response.json();
  } catch {
    // non-JSON; normalize
    data = { error: response.statusText };
  }

  if (!response.ok) {
    throw new ApiError(data.error || `HTTP ${response.status}`, response.status);
  }

  return data;
}

// Authentication API
export const authApi = {
  async login(credentials: LoginRequest): Promise<LoginResponse> {
    const response = await apiRequest<LoginResponse>("/api/auth/login", {
      method: "POST",
      body: JSON.stringify(credentials),
    });

    if (response.success && response.data) {
      setToken(response.data.token);
      return response.data;
    }

    throw new ApiError("Login failed");
  },

  async logout(): Promise<void> {
    try {
      await apiRequest("/api/auth/logout", { method: "POST" }).catch(() => {});
    } finally {
      setToken(null);
    }
  },

  getToken,
};

// Diagrams API
export const diagramsApi = {
  async getAll(
    page = 1,
    limit = 20
  ): Promise<{
    diagrams: any[];
    pagination: {
      page: number;
      limit: number;
      total: number;
      pages: number;
    };
  }> {
    const response = await apiRequest(`/api/diagrams?page=${page}&limit=${limit}`);

    if (response.success && response.data) return response.data;
    throw new ApiError("Failed to fetch diagrams");
  },

  async getById(id: string): Promise<any> {
    const response = await apiRequest(`/api/diagrams/${id}`);
    if (response.success && response.data) return response.data;
    throw new ApiError("Failed to fetch diagram");
  },

  async create(diagram: any): Promise<{ id: string; createdAt: string; updatedAt: string }> {
    const response = await apiRequest("/api/diagrams", {
      method: "POST",
      body: JSON.stringify(diagram),
    });
    if (response.success && response.data) return response.data;
    throw new ApiError("Failed to create diagram");
  },

  async update(id: string, diagram: any): Promise<{ id: string; updatedAt: string }> {
    const response = await apiRequest(`/api/diagrams/${id}`, {
      method: "PUT",
      body: JSON.stringify(diagram),
    });
    if (response.success && response.data) return response.data;
    throw new ApiError("Failed to update diagram");
  },

  async delete(id: string): Promise<void> {
    const response = await apiRequest(`/api/diagrams/${id}`, { method: "DELETE" });
    if (!response.success) throw new ApiError("Failed to delete diagram");
  },

  async duplicate(id: string, name?: string): Promise<{ id: string; name: string; createdAt: string }> {
    const response = await apiRequest(`/api/diagrams/${id}/duplicate`, {
      method: "POST",
      body: JSON.stringify({ name }),
    });
    if (response.success && response.data) return response.data;
    throw new ApiError("Failed to duplicate diagram");
  },
};

// Events API
export const eventsApi = {
  async log(event: { type: string; diagramId?: string; payload?: any }): Promise<{ id: string; createdAt: string }> {
    const response = await apiRequest("/api/events", { method: "POST", body: JSON.stringify(event) });
    if (response.success && response.data) return response.data;
    throw new ApiError("Failed to log event");
  },

  async getAll(filters?: { page?: number; limit?: number; type?: string; diagramId?: string; startDate?: string; endDate?: string }): Promise<{
    events: any[];
    pagination: { page: number; limit: number; total: number; pages: number };
  }> {
    const params = new URLSearchParams();
    if (filters) Object.entries(filters).forEach(([k, v]) => v !== undefined && params.append(k, String(v)));
    const response = await apiRequest(`/api/events?${params.toString()}`);
    if (response.success && response.data) return response.data;
    throw new ApiError("Failed to fetch events");
  },

  async getStats(filters?: { startDate?: string; endDate?: string; diagramId?: string }): Promise<{
    typeStats: { type: string; count: number }[];
    dailyStats: { date: string; count: number }[];
    topDiagrams: { diagram_id: string; event_count: number }[];
    generatedAt: string;
  }> {
    const params = new URLSearchParams();
    if (filters) Object.entries(filters).forEach(([k, v]) => v !== undefined && params.append(k, String(v)));
    const response = await apiRequest(`/api/events/stats?${params.toString()}`);
    if (response.success && response.data) return response.data;
    throw new ApiError("Failed to fetch event stats");
  },
};

export function handleApiError(error: any): never {
  if (error.status === 401 || (typeof error.message === "string" && error.message.includes("401")) || (error.message?.includes && error.message.includes("Authentication required"))) {
    localStorage.removeItem(TOKEN_KEY);
    window.location.href = "/";
  }
  throw new ApiError(error.message || "An unexpected error occurred", error.status, error.code);
}

export default { auth: authApi, diagrams: diagramsApi, events: eventsApi, handleApiError };
